import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import downloadObjectGridExcel from './objectGridExcelExport';

// BASE ENDPOINT CLASS FOR ALL FLOW OBJECTS - corresponds to BaseFlowObjectViewSet 
// you can also manually add methods to the endpoint as needed

export default class FlowObjectViewsetEndpoint {
    constructor({
        modelClass,  // name of backend model class - such as 'QualityIssue'
        flowSubtype,  // flow subtype slug, IF USED
        generalUrl,  // url for the viewset as registered in router 
        //enabledActions,  // array of standard actions that you want ENABLED - if you omit this altogether from constructor params, then all will be enabled
        //disabledActions,  // array of standard actions you want to DISABLE - THIS WINS IF YOU HAVE IN BOTH enabled and disabled
        axiosOptions,  // optional object to pass to axios.create() (merge with our defaults)
    }) {

        this.modelClass = modelClass;
        this.flowSubtype = flowSubtype || null;  // default to null instead of undefined or ''

        // CREATE AXIOS INSTANCE
        // each endpoint will have its own axios instance, 
        // so it can manage its baseURL, any special cookies, headers, etc.
        const axiosDefaults = {
            baseURL: '/api/',
            xsrfCookieName: window?.splData?.CSRF_COOKIE_NAME || 'csrftoken',
            xsrfHeaderName: 'X-CSRFTOKEN',
            headers: {...window?.splFixedHeaders || {}}
        };
        const extraAxiosOptions = axiosOptions ? cloneDeep(axiosOptions) : {};
        // probably don't need that cloneDeep... but paranoid about mutating things across instances.... and this shouldn't get called often...
        const finalAxiosOptions = Object.assign({}, axiosDefaults, extraAxiosOptions);
        this.axios = axios.create(finalAxiosOptions);
        if (this.flowSubtype) {
            // if we have a flowSubtype, we will pass it as a query param on all requests
            //     the backend expects it, in camelCase on purpose
            const params = this.axios.defaults.params || {};
            params['flowSubtype'] = this.flowSubtype;
            this.axios.defaults.params = params;
        }

        this.generalUrl = generalUrl;  // generalUrl is the URL for this object, with trailing slash

    }
    objectUrl(id) {
        // return the DETAIL URL for a specific resource instance
        // has resource ID + trailing slash:  like rfxs/6/
        return this.generalUrl + id.toString() + '/';
    }
    // ACTION HANDLERS - 'hook up' if they are in 'enabledActions'
    list(config) {
        return this.axios.get(this.generalUrl, config);
    }
    retrieve(id, config) {
        return this.axios.get(this.objectUrl(id), config);
    }
    // EXTRA actions
    get_start_nodes(config) {
        return this.axios.get(this.generalUrl + 'get_start_nodes/', config);
    }
    get_start_node_form(config) {
        return this.axios.get(this.generalUrl + 'get_start_node_form/', config);
    }
    submit_start_node_form(postData, config) {
        return this.axios.post(this.generalUrl + 'submit_start_node_form/', postData, config);
    }
    get_possible_transitions(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_possible_transitions/', config);
    }
    get_transition_detail(id, trigger) {
        // must pass in 'trigger' in query_params - the trigger name
        // this can return a 409 if transition is no longer available, or duplicate available triggers
        const axiosConfig = {params: {trigger: trigger}};
        return this.axios.get(this.objectUrl(id) + 'get_transition_detail/', axiosConfig);
    }
    get_visible_nodes(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_visible_nodes/', config);
    }
    get_node_detail(id, nodeKey) {
        // nodeKey should be the COMBINED key (key + sub_key if any)
        const axiosConfig = {params: {key: nodeKey}};
        return this.axios.get(this.objectUrl(id) + 'get_node_detail/', axiosConfig);
    }
    call_transition(id, postData, config) {  // FLOW transition
        return this.axios.post(this.objectUrl(id) + 'call_transition/', postData, config);
    }
    call_node_transition(id, postData, config) {  // NODE transition
        return this.axios.post(this.objectUrl(id) + 'call_node_transition/', postData, config);
    }
    get_change_assignee_options(id, nodeKey) {
        const axiosConfig = {  // maybe move this to components...
            params: {
                key: nodeKey,
            },
        };
        return this.axios.get(this.objectUrl(id) + 'get_change_assignee_options/', axiosConfig);
    }
    get_task_form(id, nodeKey, formKey) {
        const axiosConfig = {  // maybe move this to components...
            params: {
                node_key: nodeKey,
                form_key: formKey,
            },
        };
        return this.axios.get(this.objectUrl(id) + 'get_task_form/', axiosConfig);
    }
    submit_task_form_data(id, postData, config) {
        return this.axios.post(this.objectUrl(id) + 'submit_task_form_data/', postData, config);
    }
    get_files(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_files/', config);
    }
    get_filemanager_data(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_filemanager_data/', config);
    }
    // OBJECT ROLES
    get_team_manager_data(id, key) {
        const axiosConfig = key ? {params: {key: key}} : null;  // specifying a key is optional
        return this.axios.get(this.objectUrl(id) + 'get_team_manager_data/', axiosConfig);
    }  // kinda replaces the ones below
    get_object_roles(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_object_roles/', config);
    }
    get_object_tus(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_object_tus/', config);
    }
    get_possible_add_tus(id, role) {
        const axiosConfig = role ? {params: {role: role}} : null;  // specifying a role is optional
        return this.axios.get(this.objectUrl(id) + 'get_possible_add_tus/', axiosConfig);
    }
    get_possible_roles_for_tu(id, tuId) {
        const axiosConfig = {params: {tu: tuId}};
        return this.axios.get(this.objectUrl(id) + 'get_possible_roles_for_tu/', axiosConfig);
    }
    get_event_history(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_event_history/', config);
    }
    get_my_open_tasks({page_size = 10, page = 1, ordering = '-deadline'} = {}) {
        // for now, ordering can only be 'deadline' or '-deadline' -- expand later and coordinate w/ backend
        const config = {params: {page_size, page, ordering}};
        return this.axios.get(this.generalUrl + 'get_my_open_tasks/', config);
    }
    list_meta(config) {
        return this.axios.get(this.generalUrl + 'list_meta/', config);
    }
    get_filter_list(config) {
        return this.axios.get(this.generalUrl + 'get_filter_list/', config);
    }
    get_analytics_columns(config) {
        return this.axios.get(this.generalUrl + 'get_analytics_columns/', config);
    }
    get_analytics_compare_fields(config) {
        return this.axios.get(this.generalUrl + 'get_analytics_compare_fields/', config);
    }
    get_flow_definition(config) {
        return this.axios.get(this.generalUrl + 'get_flow_definition/', config);
    }
    get_related_instances(id, config) {
        return this.axios.get(this.objectUrl(id) + 'get_related_instances/', config);
    }
    provide_grid_data({gridRequest, include, exclude, fixedFilterKwargs}) {
        // postData should be the ag-grid request object - query params have include/exclude fields
        // calling it fixedFilterKwargs because this is different from the filterModel from the ag-grid
        // FOR NOW: we merge fixedFilterKwargs into the gridRequest, then pop off in backend... its our thing, not an ag-grid thing
        // TODO - nest things in postData better, so we can have more than just ag-grid stuff at top level
        const postData = {...gridRequest, fixed_filter_kwargs: fixedFilterKwargs || {}};
        const queryParams = {};
        if (include) {
            queryParams['include_fields'] = include;
        }
        if (exclude) {
            queryParams['exclude_fields'] = exclude;
        }
        const config = {params: queryParams};
        return this.axios.post(this.generalUrl + 'provide_grid_data/', postData, config);
    }
    provide_grid_meta({include, exclude, fixedFilterKwargs}) {
        // NEW VERSION OF GRID META - using POST instead of GET, and sending more data
        // calling it fixedFilterKwargs because this is different from the filterModel from the ag-grid
        const postData = {
            include_fields: include || [], 
            exclude_fields: exclude || [],
            fixed_filter_kwargs: fixedFilterKwargs || {},
        };
        const queryParams = {};
        const config = {params: queryParams};
        // query params have include/exclude fields
        return this.axios.post(this.generalUrl + 'provide_grid_meta/', postData, config);
    }
    // get_grid_meta({include, exclude}) {
    //     const queryParams = {};
    //     if (include) {
    //         queryParams['include_fields'] = include;
    //     }
    //     if (exclude) {
    //         queryParams['exclude_fields'] = exclude;
    //     }
    //     const config = {params: queryParams};
    //     // query params have include/exclude fields
    //     return this.axios.get(this.generalUrl + 'get_grid_meta/', config);
    // }
    async download_object_grid_excel({gridRequest, include, exclude, visibleFields, fixedFilterKwargs}) {
        const url = this.generalUrl + 'download_object_grid_excel/';
        await downloadObjectGridExcel(this.axios, url, {gridRequest, include, exclude, visibleFields, fixedFilterKwargs});
    }
    // async download_object_grid_excel_old({gridRequest, include, exclude, visibleFields, fixedFilterKwargs}) {
    //     // NOTE: this was replaced by the downloadObjectGridExcel function above - keeping it DRY
    //     // postData will include all the things - instead of making things messy (and super long URL) by passing some things as query params
    //     // this function actually does the full download, no need to do much else in caller
    //     const postData = {};
    //     postData['grid_ssrm_request'] = gridRequest;
    //     // NOTE - might be too long of URL to put all this stuff in queryParams!  visible_fields can get super long - so put all in POST body instead!
    //     if (include) {
    //         postData['include_fields'] = include;
    //     }
    //     if (exclude) {
    //         postData['exclude_fields'] = exclude;
    //     }
    //     if (visibleFields) {
    //         postData['visible_fields'] = visibleFields;
    //     }
    //     if (fixedFilterKwargs) {
    //         postData['fixed_filter_kwargs'] = fixedFilterKwargs
    //     }
    //     const config = {responseType: 'blob'};
    //     try {
    //         // make the API call, setting responseType to 'blob' to make sure we treat response data as binary
    //         const resp = await this.axios.post(this.generalUrl + 'download_object_grid_excel/', postData, config);
    //         // Create a new Blob object using the response data, with MIME-type as the .xlsx one
    //         const blob = new Blob([resp.data], {
    //             type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    //         });
        
    //         // Create a URL for the Blob object
    //         const downloadUrl = window.URL.createObjectURL(blob);

    //         // get the filename -- it SHOULD be somewhere inside 'Content-Disposition' header
    //         // such as:  Content-Disposition: attachment; filename="sub2_data_export_2025-02-01-233941.xlsx"
    //         //const contentDispHeader = 'attachment; filename="sub2_data_export_2025-02-01-233941.xlsx"';
    //         const contentDispHeader = resp.headers['content-disposition'] || '';
    //         const regex = /filename="([^"]+)"/;
    //         const matches = contentDispHeader.match(regex);
    //         const finalFilename = matches && matches[1] ? matches[1] : 'Supplios_export.xlsx';
        
    //         // Create a temporary <a> element to trigger the download
    //         const link = document.createElement('a');
    //         link.href = downloadUrl;
    //         link.setAttribute('download', finalFilename); // Set the desired file name
        
    //         // Append the link to the document body and trigger the download
    //         document.body.appendChild(link);
    //         link.click();
        
    //         // Clean up and remove the link
    //         setTimeout(() => {
    //             link.remove();
    //             window.URL.revokeObjectURL(downloadUrl);
    //         }, 100);

    //     } catch (error) {
    //         console.error('Error in download_object_grid_excel');
    //         console.error(error);
    //     }
    // }
}
